import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  RotateCw, Gift, Users, Gem, Rocket, Crown, 
  TrendingUp, TrendingDown, Coins
} from 'lucide-react';
import type { WheelAdminStats } from '@/hooks/useAdminWheelData';

interface WheelStatsCardsProps {
  stats: WheelAdminStats;
}

const WheelStatsCards: React.FC<WheelStatsCardsProps> = ({ stats }) => {
  const isProfitable = stats.profitGems >= 0;

  return (
    <div className="space-y-4">
      {/* Primeira linha: Giros */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-blue-900/30 border border-blue-500/30">
                <RotateCw className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">Total Giros</p>
                <p className="text-lg sm:text-2xl font-bold text-white">{stats.totalSpins}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-green-900/30 border border-green-500/30">
                <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">Giros Grátis</p>
                <p className="text-lg sm:text-2xl font-bold text-white">{stats.freeSpins}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-purple-900/30 border border-purple-500/30">
                <Gem className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">Giros Pagos</p>
                <p className="text-lg sm:text-2xl font-bold text-white">{stats.paidSpins}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-cyan-900/30 border border-cyan-500/30">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">Usuários</p>
                <p className="text-lg sm:text-2xl font-bold text-white">{stats.uniqueUsers}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Segunda linha: Economia */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-emerald-900/30 border border-emerald-500/30">
                <Coins className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">Gemas Gastas</p>
                <p className="text-lg sm:text-2xl font-bold text-white">{stats.totalGemsSpent}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-pink-900/30 border border-pink-500/30">
                <Gem className="w-4 h-4 sm:w-5 sm:h-5 text-pink-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">Gemas Dadas</p>
                <p className="text-lg sm:text-2xl font-bold text-white">{stats.totalGemsWon}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-yellow-900/30 border border-yellow-500/30">
                <Rocket className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">Boosts</p>
                <p className="text-lg sm:text-2xl font-bold text-white">{stats.totalBoostsGiven}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-luxury-gold/30 border border-luxury-gold/30">
                <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-luxury-gold" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">Dias VIP</p>
                <p className="text-lg sm:text-2xl font-bold text-white">{stats.totalVipDaysGiven}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Card de Lucro/Prejuízo destacado */}
      <Card className={`border backdrop-blur-sm ${isProfitable ? 'bg-green-900/20 border-green-500/30' : 'bg-red-900/20 border-red-500/30'}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isProfitable ? 'bg-green-900/30 border border-green-500/30' : 'bg-red-900/30 border border-red-500/30'}`}>
                {isProfitable ? (
                  <TrendingUp className="w-5 h-5 text-green-400" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-red-400" />
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {isProfitable ? 'Lucro' : 'Prejuízo'} da Roleta
                </p>
                <div className="flex items-center gap-3">
                  <p className={`text-2xl font-bold ${isProfitable ? 'text-green-400' : 'text-red-400'}`}>
                    {isProfitable ? '+' : ''}{stats.profitGems} 💎
                  </p>
                  <p className={`text-lg ${isProfitable ? 'text-green-400/60' : 'text-red-400/60'}`}>
                    ({isProfitable ? '+' : ''}R$ {stats.profitBRL.toFixed(2)})
                  </p>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground max-w-[150px] text-right">
              Baseado em R$ 0,50/gema
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WheelStatsCards;
